import { markRaw } from 'vue'
import { defineStore } from 'pinia'
import {
  OrderSide,
  TradeDirection,
  TradeExecutionType
} from '@injectivelabs/sdk-ts/types/light'
import { TpSlTriggerType } from '@/types/rfq'
import type {
  OrderState,
  TcDerivativeLimitOrder,
  TcDerivativeOrderHistory,
  TcDerivativeTradeHistory
} from '@injectivelabs/sdk-ts'
import type { UnifiedOpenOrder } from '@/types/trade'

const importSubaccountStream = () => import('./stream')
const importSubaccountActions = () => import('./actions')

type SubaccountStoreState = {
  ordersCursors: string[]
  positionsCursors: string[]
  orderHistoryCursors: string[]
  tradeHistoryCursors: string[]
  subaccountOrders: TcDerivativeLimitOrder[]
  subaccountTrades: TcDerivativeTradeHistory[]
  subaccountOrderHistory: TcDerivativeOrderHistory[]
}

const initialStateFactory = (): SubaccountStoreState => ({
  ordersCursors: [],
  positionsCursors: [],
  orderHistoryCursors: [],
  tradeHistoryCursors: [],
  subaccountTrades: markRaw([] as TcDerivativeTradeHistory[]),
  subaccountOrders: markRaw([] as TcDerivativeLimitOrder[]),
  subaccountOrderHistory: markRaw([] as TcDerivativeOrderHistory[])
})

export const useSubaccountStore = defineStore('subaccount', {
  state: (): SubaccountStoreState => initialStateFactory(),
  getters: {
    subaccountConditionalOrders: (): UnifiedOpenOrder[] => {
      const positionStore = usePositionStore()
      const conditionalOrders = Object.values(positionStore.tpSlOrders).flat()

      return conditionalOrders.map((tpSlOrder) => {
        const isClosingShort = tpSlOrder.direction === 'short'
        const isMarkPriceGte =
          tpSlOrder.triggerType === TpSlTriggerType.MarkPriceGte

        const orderType = isMarkPriceGte
          ? isClosingShort
            ? OrderSide.TakeSell
            : OrderSide.StopBuy
          : isClosingShort
            ? OrderSide.StopSell
            : OrderSide.TakeBuy

        const positionDirection = isClosingShort
          ? TradeDirection.Long
          : TradeDirection.Short

        const selectedPosition = positionStore.positions.find(
          (position) =>
            position.marketId === tpSlOrder.marketId &&
            position.direction === positionDirection
        )

        return {
          orderType,
          triggerAt: 0,
          orderHash: '',
          orderNumber: 0,
          feeRecipient: '',
          subaccountId: '',
          isReduceOnly: false,
          isConditional: true,
          placedOrderHash: '',
          orderSide: orderType,
          state: '' as OrderState,
          margin: tpSlOrder.margin,
          position: selectedPosition,
          quantity: tpSlOrder.quantity,
          marketId: tpSlOrder.marketId,
          price: tpSlOrder.triggerPrice,
          createdAt: tpSlOrder.createdAt,
          updatedAt: tpSlOrder.updatedAt,
          cid: tpSlOrder.rfqId.toString(),
          unfilledQuantity: tpSlOrder.quantity,
          triggerPrice: tpSlOrder.triggerPrice,
          executionType: TradeExecutionType.Market,
          accountAddress: tpSlOrder.requestAddress
        }
      })
    },

    subaccountUnifiedOrders: (state): UnifiedOpenOrder[] => {
      const subaccountStore = useSubaccountStore()

      return [
        ...state.subaccountOrders,
        ...subaccountStore.subaccountConditionalOrders
      ]
    }
  },
  actions: {
    setOrdersCursors(ordersCursors: string[]) {
      this.ordersCursors = ordersCursors
    },

    setPositionsCursors(positionsCursors: string[]) {
      this.positionsCursors = positionsCursors
    },

    setOrderHistoryCursors(orderHistoryCursors: string[]) {
      this.orderHistoryCursors = orderHistoryCursors
    },

    setTradeHistoryCursors(tradeHistoryCursors: string[]) {
      this.tradeHistoryCursors = tradeHistoryCursors
    },

    setSubaccountOrderHistory(
      subaccountOrderHistory: TcDerivativeOrderHistory[]
    ) {
      this.subaccountOrderHistory = markRaw(subaccountOrderHistory)
    },

    setSubaccountOrders(subaccountOrders: TcDerivativeLimitOrder[]) {
      this.subaccountOrders = markRaw(subaccountOrders)
    },

    setSubaccountTrades(subaccountTrades: TcDerivativeTradeHistory[]) {
      this.subaccountTrades = markRaw(subaccountTrades)
    },

    streamTcDerivativeOrders: lazyPiniaAction(
      importSubaccountStream,
      'streamTcDerivativeOrders'
    ),

    streamTcDerivativeOrderHistory: lazyPiniaAction(
      importSubaccountStream,
      'streamTcDerivativeOrderHistory'
    ),

    streamTcDerivativeTradesHistory: lazyPiniaAction(
      importSubaccountStream,
      'streamTcDerivativeTradesHistory'
    ),

    cancelTcDerivativeOrdersStream: lazyPiniaAction(
      importSubaccountStream,
      'cancelTcDerivativeOrdersStream'
    ),

    cancelTcDerivativeOrderHistoryStream: lazyPiniaAction(
      importSubaccountStream,
      'cancelTcDerivativeOrderHistoryStream'
    ),

    cancelTcDerivativeTradesHistoryStream: lazyPiniaAction(
      importSubaccountStream,
      'cancelTcDerivativeTradesHistoryStream'
    ),

    fetchTcDerivativeOrders: lazyPiniaAction(
      importSubaccountActions,
      'fetchTcDerivativeOrders'
    ),

    fetchTcDerivativeOrdersHistory: lazyPiniaAction(
      importSubaccountActions,
      'fetchTcDerivativeOrdersHistory'
    ),

    fetchTcDerivativeTradesHistory: lazyPiniaAction(
      importSubaccountActions,
      'fetchTcDerivativeTradesHistory'
    ),

    cancelTcDerivativeStreams: lazyPiniaAction(
      importSubaccountActions,
      'cancelTcDerivativeStreams'
    ),

    resetSubaccount: lazyPiniaAction(
      importSubaccountActions,
      'resetSubaccount'
    ),

    reset() {
      const subaccountStore = useSubaccountStore()
      const {
        ordersCursors,
        positionsCursors,
        subaccountTrades,
        subaccountOrders,
        orderHistoryCursors,
        tradeHistoryCursors,
        subaccountOrderHistory
      } = initialStateFactory()

      subaccountStore.setOrdersCursors(ordersCursors)
      subaccountStore.setPositionsCursors(positionsCursors)
      subaccountStore.setSubaccountTrades(subaccountTrades)
      subaccountStore.setSubaccountOrders(subaccountOrders)
      subaccountStore.setOrderHistoryCursors(orderHistoryCursors)
      subaccountStore.setTradeHistoryCursors(tradeHistoryCursors)
      subaccountStore.setSubaccountOrderHistory(subaccountOrderHistory)
    }
  }
})
